import { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDutchiePay } from 'src/payments/hooks';
import useUser from 'shared/hooks/use-user';
import useStores from 'shared/hooks/use-stores';
import { tracker } from '..';
import { GTM_V2_FLAG } from '../constants/feature-flags';

export const useViewedCheckoutMonitor = (): void => {
  const { isEnrolledDutchiePay, isDutchiePayEnabledForDispo } = useDutchiePay();
  const flags = useFlags();
  const useV2 = !!flags[GTM_V2_FLAG];
  const user = useUser();
  const userId = useObserver(() => user?.id);
  const customerId = userId;
  const loggedIn = useObserver(() => user?.isLoggedIn);
  const isSsoUser = useObserver(() => user?.isSsoUser);
  const isLoggedInViaExternalProvider = isSsoUser;
  const userLoading = useObserver(() => user?.loading);
  const { Cart, UI } = useStores();
  const checkoutToken = useObserver(() => Cart?.checkoutToken);
  const coupon = useObserver(() => Cart?.coupon);

  const { dispensary } = Cart?.order ?? {};
  const { id: dispensaryId, name: dispensaryName } = dispensary ?? {};
  const subtotalValue = useV2 ? Cart.costBreakdown.total : Cart.costBreakdown.subtotal;
  const taxCents = useObserver(() => Cart?.costBreakdown?.receipt?.summary?.taxes);
  const tax = taxCents != null ? parseFloat((taxCents / 100).toFixed(2)) : undefined;
  const taxRef = useRef(tax);
  taxRef.current = tax;
  const summaryDiscounts = useObserver(() => Cart?.costBreakdown?.receipt?.summary?.discounts);
  const summaryMixAndMatch = useObserver(() => Cart?.costBreakdown?.receipt?.summary?.mixAndMatch);
  const summaryRewards = useObserver(() => Cart?.costBreakdown?.receipt?.summary?.rewards);

  useEffect(() => {
    if (userId === undefined || dispensary === undefined || Cart?.order === undefined) {
      return;
    }

    tracker.setContext({
      activeUser: {
        id: userId,
      },
      activeDispensary: dispensary,
      activeCart: { subtotal: subtotalValue, ...Cart.order.cart },
      activeOrder: Cart.order,
      cartDiscountSummary:
        summaryDiscounts != null || summaryMixAndMatch != null || summaryRewards != null
          ? {
              discounts: summaryDiscounts ?? 0,
              mixAndMatch: summaryMixAndMatch ?? 0,
              rewards: summaryRewards ?? 0,
            }
          : null,
      checkoutToken: Cart.checkoutToken,
      isKioskOrder: Cart.isKioskOrder,
      activeMenuVariant: UI?.variant,
    });
  }, [
    Cart,
    userId,
    dispensary,
    Cart.checkoutToken,
    Cart.order,
    coupon,
    subtotalValue,
    Cart.isKioskOrder,
    UI?.variant,
    summaryDiscounts,
    summaryMixAndMatch,
    summaryRewards,
  ]);

  useEffect(() => {
    const payloadProps = [
      checkoutToken,
      dispensaryId,
      dispensaryName,
      isDutchiePayEnabledForDispo,
      isEnrolledDutchiePay,
    ];

    if (payloadProps.some((value) => value === undefined)) {
      return;
    }

    tracker.viewedCheckout({
      checkoutToken,
      dispensaryId,
      dispensaryName,
      isDutchiePayEnabledForDispo,
      isEnrolledDutchiePay,
      tax: taxRef.current,
    });
  }, [checkoutToken, dispensaryId, dispensaryName, isDutchiePayEnabledForDispo, isEnrolledDutchiePay]);

  useEffect(() => {
    const payloadProps = [
      checkoutToken,
      dispensaryId,
      dispensaryName,
      isDutchiePayEnabledForDispo,
      isEnrolledDutchiePay,
      customerId,
      loggedIn,
    ];

    if (payloadProps.some((value) => value === undefined)) {
      return;
    }

    if (loggedIn && userLoading === false && checkoutToken && !isLoggedInViaExternalProvider) {
      tracker.checkoutSession({
        checkoutToken,
        dispensaryId,
        dispensaryName,
        isDutchiePayEnabledForDispo,
        isEnrolledDutchiePay,
        customerId,
        loggedIn,
        tax: taxRef.current,
      });
    }
  }, [
    checkoutToken,
    dispensaryId,
    dispensaryName,
    isDutchiePayEnabledForDispo,
    isEnrolledDutchiePay,
    customerId,
    loggedIn,
    userLoading,
    isLoggedInViaExternalProvider,
  ]);
};
